import {
  Box,
  Typography,
  Paper,
  Button,
  Dialog,
  IconButton,
  useMediaQuery,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import ctaButtonStyle from "../theme/ctaButtonStyle";

function BackgroundTemplateSelector({
  templates,              // [{ key: heroImg, value: pdfImg }]
  selectedBg,             // selected value (pdf image)
  onSelect,               // returns value 
  showSeeMore = false,
  enablePreview = false,
  navigateOnSelect = false,   // ✅ NEW
  title = "Select Template",
  subtitle = "",
}) {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isDark = theme.palette.mode === "dark";

  const sectionGradient = isDark
    ? "linear-gradient(180deg, #1b1f27 0%, #0f1218 100%)"
    : "linear-gradient(180deg, #fff7f2 0%, #ffffff 100%)";

  const [previewKey, setPreviewKey] = useState(null);  // hero image
  const [selectedKey, setSelectedKey] = useState(null);

  /* Sync UI selection if selectedBg changes externally */
useEffect(() => {
  const matched = templates.find((t) => t.value === selectedBg);
  if (matched) setSelectedKey(matched.key);
}, [selectedBg, templates]);


const handleSelect = (key, value) => {
  setSelectedKey(key);

  if (navigateOnSelect) {
    navigate("/create-biodata", {
      state: { selectedBg: value },
    });
  } else {
    onSelect({ key, value });
  }
};

  return (
    <>
      <Box
        id="templates"
        mt={6}
        mb={6}
        px={{ xs: 1, sm: 2 }}
        sx={{
          position: "relative",
          background: isDark
            ? "linear-gradient(180deg, rgba(30,34,44,0.9), rgba(15,18,24,0.95))"
            : "linear-gradient(180deg, #fff5ef, #ffffff)",
          borderRadius: 6,
          boxShadow: isDark
            ? "0 20px 60px rgba(0,0,0,0.55)"
            : "0 20px 50px rgba(255,150,100,0.25)",
        }}
      >
        {/* Title */}
        <Typography
          align="center"
          sx={{
            fontSize: { xs: "1.35rem", md: "1.6rem" },
            fontWeight: 800,
            letterSpacing: "0.7px",
            textTransform: "uppercase",
            mb: 1.5,
          }}
        >
          {title}
        </Typography>

        {subtitle && (
          <Typography
            align="center"
            sx={{
              fontSize: "0.95rem",
              color: "#666",
              maxWidth: 520,
              mx: "auto",
              mb: 4,
              lineHeight: 1.6,
            }}
          >
            {subtitle}
          </Typography>
        )}

        {/* Grid */}
        <Box
          sx={{
            background: sectionGradient,
            borderRadius: 4,
            p: { xs: 2, sm: 3 },
            maxWidth: 980,
            mx: "auto",
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
              gap: 3,
              justifyItems: "center",
            }}
          >
            {templates.map(({ key, value }) => {
              const isSelected = selectedKey === key;

              return (
                <Paper
                  key={key}
                  elevation={isSelected ? 10 : 2}
                  sx={{
                    width: 160,
                    height: 225,
                    borderRadius: 3,
                    position: "relative",
                    overflow: "hidden",
                    cursor: "pointer",
                    backgroundImage: `url(${key})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    transition: "all 0.3s ease",
                    border: isSelected
                      ? "3px solid #FF9966"
                      : "1px solid rgba(0,0,0,0.08)",

                    "&:hover": {
                      transform: "translateY(-6px)",
                      boxShadow: "0 18px 32px rgba(0,0,0,0.25)",
                    },

                    "&:hover .overlay": {
                      opacity: 1,
                    },
                  }}
                    onClick={() => {
                   //   if (enablePreview) {
                        setPreviewKey(key); // ✅ ALWAYS open preview first
                      // } else {
                      //   handleSelect(key, value);
                      // }
                    }}
                >
                  {isSelected && (
                    <Chip
                      icon={<CheckCircleIcon />}
                      label="Selected"
                      size="small"
                      sx={{
                        position: "absolute",
                        top: 10,
                        left: 10,
                        background:
                          "linear-gradient(90deg, #FF9966, #FF5E62)",
                        color: "#fff",
                        fontWeight: 600,
                        zIndex: 3,
                      }}
                    />
                  )}

                  {enablePreview && !isMobile && (
                    <Box
                      className="overlay"
                      sx={{
                        position: "absolute",
                        inset: 0,
                        background:
                          "linear-gradient(180deg, rgba(0,0,0,0.65), rgba(0,0,0,0.85))",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        opacity: 0,
                        transition: "opacity 0.25s ease",
                        zIndex: 2,
                      }}
                    >
                      <Button
                        variant="contained"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setPreviewKey(key);
                        }}
                        sx={{ fontWeight: 700, px: 2.5 }}
                      >
                        Preview
                      </Button>
                    </Box>
                  )}
                </Paper>
              );
            })}
          </Box>
        </Box>

 {showSeeMore && (
  <Box
    textAlign="center"
    mt={5}
    mb={5}
    sx={{ display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap" }}
  >
    {/* ✅ Create Biodata */}
    <Button
      variant="contained"
      size="large"
      onClick={() => navigate("/create-biodata")}
      sx={ctaButtonStyle}
    >
      Create Biodata
    </Button>

    {/* ✅ More Templates */}
    <Button
      variant="outlined"
      size="large"
      onClick={() => navigate("/marriage-biodata-templates")}
      sx={{
        borderRadius: 3,
        px: 4,
        py: 1.2,
        fontWeight: 600,
        textTransform: "none",
      }}
    >
      View More Templates
    </Button>
  </Box>
)}
      </Box>

      {/* ================= Preview Dialog ================= */}
      <Dialog
        open={Boolean(previewKey)}
        onClose={() => setPreviewKey(null)}
        fullScreen={isMobile}
        maxWidth="md"
        fullWidth
        BackdropProps={{
          sx: {
            backgroundColor: "rgba(255,255,255,0.35)",
            backdropFilter: "blur(6px)",
          },
        }}
      >
        {/* Close */}
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 10,
            display: "flex",
            justifyContent: "flex-end",
            p: 1,
          }}
        >
          <IconButton
            onClick={() => setPreviewKey(null)}
            sx={{
              backgroundColor: "rgba(0,0,0,0.7)",
              color: "#fff",
              width: 44,
              height: 44,
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Preview Content */}
        <Box sx={{ p: { xs: 1, sm: 2 } }}>
          <Box
            sx={{
              width: "100%",
              aspectRatio: "210 / 297",
              backgroundImage: `url(${previewKey})`,
              backgroundSize: "contain",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "center",
              borderRadius: 3,
              border:
                selectedKey === previewKey
                  ? "3px solid #FF9966"
                  : "1px solid #ddd",
              minHeight: isMobile ? "85vh" : 540,
              backgroundColor: "#fff",
              transition: "all 0.3s ease",
            }}
          />
        </Box>

        {/* Select Button Inside Preview */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            pb: 3,
          }}
        >
<Button
  onClick={() => {
    const matched = templates.find(
      (t) => t.key === previewKey
    );

    if (!matched) return;

    if (navigateOnSelect) {
      navigate("/create-biodata", {
        state: {
          selectedBg: matched.value,
          theme: matched.theme,
        },
      });
    } else {
      onSelect(matched);
    }

    setPreviewKey(null);
  }}

            sx={ctaButtonStyle}
          >
            {selectedKey === previewKey
              ? "Selected"
              : "Select Template"}
          </Button>
        </Box>
      </Dialog>
    </>
  );
}

export default BackgroundTemplateSelector;
